import { FormField } from '@bonniernews/dn-design-system-web/assets/form-field/form-field.tsx'
import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'

export interface SelectProps extends SharedProps {
  /** It's used as name and id */
  name: string
  /**  A label for the input */
  label: string
  /** Used if error state shoud be set on load */
  error?: boolean
  /** What error message that should be displayed */
  errorMessage?: string
  required?: boolean
  /** Set to true if the field should be disabled */
  disabled?: boolean
  /** To use with the sites validation, some example values for dn-web: required, password, email */
  validation?: string
  /** A helptext for the field if needed */
  helpText?: string
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
  /** Set to true if field should have focus when loaded */
  autofocus?: boolean
  /** Ex. { data-prop: value } */
  options?: { value: string; label: string }[]
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/select](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/select)
 * - Storybook: [Form > Select](https://designsystem.dn.se/?path=/docs/basic-form-select--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use "@bonniernews/dn-design-system-web/components/select/select";`
 */
export const Select = ({
  name,
  error,
  errorMessage,
  helpText,
  forcePx,
  classNames = '',
  label,
  options,
  validation,
  disabled,
  required,
  autofocus,
}: SelectProps) => {
  const componentClassName = 'ds-select'
  const wrapperClasses = formatClassString([componentClassName, forcePx && 'ds-force-px', classNames])

  return (
    <FormField {...{ error, errorMessage, helpText, wrapperClasses }}>
      {/* This wrapper is needed since errorMessage can be injected into FormField */}
      <div className='ds-select__inner'>
        <IconUse iconName={'expand_more'} />
        <label htmlFor={name} className={`${componentClassName}__label`}>
          {label}
        </label>
        <select
          id={name}
          name={name}
          className={`${componentClassName}__input`}
          data-validation={validation}
          disabled={disabled}
          required={required}
          autoFocus={autofocus}
        >
          <option value='' selected disabled={required}>
            {label}
          </option>

          {options?.map((option) => (
            <option key={`option-${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </FormField>
  )
}
